import {
  BlockTitle,
  Button,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  SearchInput,
  Surface,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  titleCase,
  useBreakpoint,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import { ActionsCell, SentCell, StatusCell } from './anamnesis-row'
import { useAnamneses, type AnamnesisFilter, type AnamnesisSort, type PatientContact } from './api'
import { IssueAnamnesis } from './issue-anamnesis'
import { canOpen } from './labels'
import { PatientPicker } from './patient-picker'
import { useAnamnesisActions } from './use-anamnesis-actions'

/**
 * Anamneses da clínica (docs/areas/anamnese.md, "Lista global"; RN-ANA-22). Arquétipo: lista de
 * trabalho. Abre em "Não preenchidas" enquanto houver o que não voltou; a linha preenchida abre a
 * leitura na ficha da paciente, e a não preenchida oferece reenviar. Uma ação primária:
 * "+ Enviar anamnese", que escolhe a paciente e segue para o mesmo formulário da ficha (RN-ANA-01).
 * Quem edita as perguntas da clínica (gestora, root na clínica) tem a ação secundária
 * "Perguntas da clínica", em contorno (RN-ANA-19, P-ANA-63).
 */

const FILTERS: AnamnesisFilter[] = ['all', 'completed', 'not_completed']

const SORT_NOTES: Record<AnamnesisSort, string> = {
  '-sentAt': 'envios mais recentes primeiro',
  sentAt: 'envios mais antigos primeiro',
  patient: 'em ordem de nome da paciente',
  status: 'em ordem de status',
}

export function AnamnesesPage() {
  const navigate = useNavigate()
  const session = useSession()
  // Abaixo de 900px a linha vira cartão, e o cartão começa pelo nome da paciente.
  const isDesk = useBreakpoint('desk')

  const stored = useStoredFilter('anamneses', FILTERS, 'all')
  const { setFilter } = stored
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState<AnamnesisSort>('-sentAt')
  const debouncedSearch = useDebounced(search.trim())
  // A busca da API pede de 2 a 80 caracteres: uma letra só ainda não filtra, e o excesso é cortado.
  const term = debouncedSearch.length >= 2 ? debouncedSearch.slice(0, 80) : ''
  // A tela abre no que tem pendência (RN-ANA-22), até a pessoa escolher um segmento: o número de
  // não preenchidas vem na primeira resposta, e a lista troca sozinha para elas.
  const [pendingSeen, setPendingSeen] = useState(0)
  const filter: AnamnesisFilter =
    !stored.hasChosen && pendingSeen > 0 ? 'not_completed' : stored.filter
  const query = useAnamneses({ filter, search: term || undefined, sort, page })
  const counts = query.data?.meta.counts
  const pendingCount = counts?.not_completed ?? 0
  if (pendingCount !== pendingSeen) setPendingSeen(pendingCount)

  const [sending, setSending] = useState(false)
  const [patient, setPatient] = useState<PatientContact | null>(null)
  const actions = useAnamnesisActions()

  const options: FilterSegmentOption<AnamnesisFilter>[] = [
    { value: 'all', label: 'Todas', count: counts?.all },
    { value: 'completed', label: 'Preenchidas', count: counts?.completed },
    { value: 'not_completed', label: 'Não preenchidas', count: counts?.not_completed },
  ]

  function closeSending() {
    setSending(false)
    setPatient(null)
  }

  function sortBy(next: AnamnesisSort) {
    setSort(next)
    setPage(1)
  }

  const anamneses = query.data?.data ?? []
  const isEmptyClinic = !query.isPending && !term && (counts?.all ?? 0) === 0
  const showSearch = (counts?.all ?? 0) > 20 || search !== ''
  const busy = actions.isResending || actions.isCanceling

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Operação' }, { label: 'Anamneses' }]}
        actions={
          <>
            {session.canManageClinic ? (
              <Button variant="outline" size="sm" asChild>
                <Link to={routes.anamnesisQuestionnaire}>Perguntas da clínica</Link>
              </Button>
            ) : null}
            {/* Com o envio aberto, a ação primária da tela passa a ser a do próprio formulário. */}
            {sending ? null : (
              <Button size="sm" onClick={() => setSending(true)}>
                + Enviar anamnese
              </Button>
            )}
          </>
        }
      />
      <Page wide>
        <PageTitle title="Anamneses" subtitle="Enviadas, preenchidas e o que ainda não voltou." />

        {sending ? (
          <Surface className="max-desk:px-4 mt-6">
            <BlockTitle
              title={
                patient ? `Enviar anamnese para ${titleCase(patient.name)}` : 'Enviar anamnese'
              }
              note={
                patient
                  ? 'São 5 blocos curtos, cerca de 4 minutos, no celular dela, sem cadastro nem senha. O link vale por 7 dias.'
                  : 'Escolha a paciente. O link chega no celular dela, sem cadastro nem senha.'
              }
            />
            <div className="mt-4">
              {patient ? (
                <IssueAnamnesis
                  key={patient.id}
                  primary
                  patient={patient}
                  onClose={closeSending}
                  aside={
                    <Button variant="text" onClick={() => setPatient(null)}>
                      Trocar paciente
                    </Button>
                  }
                />
              ) : (
                <PatientPicker onSelect={setPatient} onClose={closeSending} />
              )}
            </div>
          </Surface>
        ) : null}

        {actions.notice ? <div className="mt-6">{actions.notice}</div> : null}

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar as anamneses"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : isEmptyClinic ? (
          <EmptyState
            className="mt-6"
            title="Nenhuma anamnese ainda"
            description="Envie a primeira pela ficha de uma paciente. Ela responde no celular, em cerca de 4 minutos, e a resposta volta para cá com data, hora e hash."
            action={
              sending ? undefined : (
                <Button onClick={() => setSending(true)}>+ Enviar anamnese</Button>
              )
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <FilterSegments
                label="Filtrar anamneses"
                options={options}
                value={filter}
                onChange={(value) => {
                  setFilter(value)
                  setPage(1)
                }}
              />
              {showSearch ? (
                <SearchInput
                  className="tablet:max-w-[300px]"
                  value={search}
                  onChange={(value) => {
                    setSearch(value)
                    setPage(1)
                  }}
                  placeholder="Buscar por nome da paciente"
                  aria-label="Buscar anamnese"
                />
              ) : null}
            </div>

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={6} columns={4} />
              ) : anamneses.length === 0 ? (
                <EmptyState
                  title={
                    term
                      ? `Nenhuma anamnese para "${term}"`
                      : filter === 'not_completed'
                        ? 'Nenhuma anamnese não preenchida'
                        : 'Nenhuma anamnese preenchida ainda'
                  }
                  description={
                    term
                      ? 'A busca cobre o nome da paciente, dentro do filtro escolhido.'
                      : filter === 'not_completed'
                        ? 'Tudo o que foi enviado já voltou.'
                        : 'As enviadas aparecem aqui assim que a paciente responder.'
                  }
                  action={
                    term ? (
                      <Button variant="outline" onClick={() => setSearch('')}>
                        Limpar busca
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => setFilter('all')}>
                        Ver todas
                      </Button>
                    )
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead
                        sorted={sort === '-sentAt' ? 'desc' : sort === 'sentAt' ? 'asc' : null}
                        onSort={() => sortBy(sort === '-sentAt' ? 'sentAt' : '-sentAt')}
                      >
                        Envio
                      </TableHead>
                      <TableHead
                        sorted={sort === 'patient' ? 'asc' : null}
                        onSort={() => sortBy(sort === 'patient' ? '-sentAt' : 'patient')}
                      >
                        Paciente
                      </TableHead>
                      <TableHead>Procedimento</TableHead>
                      <TableHead
                        sorted={sort === 'status' ? 'asc' : null}
                        onSort={() => sortBy(sort === 'status' ? '-sentAt' : 'status')}
                      >
                        Status
                      </TableHead>
                      <TableHead aria-label="Ações" />
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {anamneses.map((anamnesis) => {
                      const sentCell = (
                        <TableCell key="sent" label="Envio" muted>
                          <SentCell anamnesis={anamnesis} />
                        </TableCell>
                      )
                      const patientCell = (
                        <TableCell key="patient" primary>
                          {/* Um filho só: no celular a célula é flex, e o nome fica acima da clínica. */}
                          <div className="min-w-0">
                            {titleCase(anamnesis.patient.name)}
                            {anamnesis.clinic ? (
                              <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light">
                                {anamnesis.clinic.name}
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                      )
                      return (
                        <TableRow
                          key={anamnesis.id}
                          critical={anamnesis.status === 'expired'}
                          onClick={
                            canOpen(anamnesis)
                              ? () =>
                                  navigate(
                                    routes.patientAnamnesis(anamnesis.patient.id, anamnesis.id),
                                  )
                              : undefined
                          }
                        >
                          {isDesk ? [sentCell, patientCell] : [patientCell, sentCell]}
                          <TableCell label="Procedimento" muted>
                            {anamnesis.procedureAtSendTime?.name ?? 'Sem procedimento'}
                          </TableCell>
                          <TableCell label="Status">
                            <StatusCell anamnesis={anamnesis} />
                          </TableCell>
                          <TableCell>
                            <ActionsCell
                              anamnesis={anamnesis}
                              busy={busy}
                              onResend={(target) => actions.resend(target)}
                              onCancel={(target) => actions.askCancel(target)}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
              {query.data && anamneses.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['anamnese', 'anamneses']}
                  note={`${SORT_NOTES[sort]} · linha preenchida abre a leitura na ficha da paciente; não preenchida oferece reenviar`}
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>

      {actions.dialog}
    </>
  )
}
